import React, { useState, useEffect, useCallback } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
    Col,
    Row,
    Card,
    Modal,
    Alert,
    Spinner,
    Badge,
    Form,
    Button as BButton,
} from "react-bootstrap";
import {
    Booking,
    GetBookingsInput,
    GetBookingsOutput,
    AddBookingInput,
    AddBookingOutput,
    UpdateBookingInput,
    UpdateBookingOutput,
    DeleteBookingInput,
    DeleteBookingOutput,
    ToggleBookingInput,
    ToggleBookingOutput,
    Availability,
    WeeklySchedule,
    TimeSlot,
} from "../../client/core";
import { Color, Path, Storage } from "../../env";
import { Button, icon, variant } from "../form/Button";
import { FaEdit, FaTrash } from "react-icons/fa";
import MultiImageUploader from "../form/MultiImageUploader";
import { v4 as uuid } from "uuid";

interface Session {
    getIdToken: () => { getJwtToken: () => string };
}

export interface BookingsProps extends RouteComponentProps {
    getBookings: (input: GetBookingsInput) => Promise<GetBookingsOutput>;
    addBooking: (input: AddBookingInput) => Promise<AddBookingOutput>;
    updateBooking: (input: UpdateBookingInput) => Promise<UpdateBookingOutput>;
    deleteBooking: (input: DeleteBookingInput) => Promise<DeleteBookingOutput>;
    toggleBooking: (input: ToggleBookingInput) => Promise<ToggleBookingOutput>;
    currentSession: () => Promise<Session>;
    imageStore: {
        put: (key: string, file: File, config: { contentType: string }) => Promise<any>;
    };
}

enum Activity {
    List,
    Add,
    View,
}

interface BookingsState {
    bookings: Booking[];
    filteredBookings: Booking[];
    isLoading: boolean;
    error: string;
    activity: Activity;
    activeBookingId: string | null;
    deletingBookingId: string | null;
}

const Bookings: React.FC<BookingsProps> = ({
    getBookings,
    addBooking,
    updateBooking,
    deleteBooking,
    toggleBooking,
    currentSession,
    history,
    imageStore,
}) => {
    const [state, setState] = useState<BookingsState>({
        bookings: [],
        filteredBookings: [],
        isLoading: true,
        error: "",
        activity: Activity.List,
        activeBookingId: null,
        deletingBookingId: null,
    });

    const fetchBookings = useCallback(async () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
                error: "Shop could not be found. Please navigate back to the dashboard.",
            }));
            return;
        }

        try {
            const response = await getBookings({
                shopID: shopID,
            });

            if (response.statusCode !== 200) {
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: "Unable to load bookings. Please try again later.",
                }));
                return;
            }

            const bookings = response.bookings || [];
            setState((prev) => ({
                ...prev,
                bookings: bookings,
                filteredBookings: bookings,
                isLoading: false,
            }));
        } catch (err) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
                error: "Unable to load bookings. Please try again later.",
            }));
        }
    }, [getBookings]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    const changeActivity = (activity: Activity) => {
        setState((prev) => ({
            ...prev,
            activity,
            error: "",
        }));
    };

    const handleBookingSearch = (input: string): void => {
        const filtered = state.bookings.filter((b) =>
            b.name.toLowerCase().includes(input.toLowerCase())
        );
        setState((prev) => ({
            ...prev,
            filteredBookings: filtered,
        }));
    };

    const handleSelectBooking = (bookingId: string) => {
        setState((prev) => ({
            ...prev,
            activeBookingId: bookingId,
            activity: Activity.View,
            error: "",
        }));
    };

    const handleAddBooking = async (booking: Booking, imageFiles: File[] = []) => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) {
            setState((prev) => ({
                ...prev,
                error: "Shop could not be found.",
            }));
            return;
        }

        let token: string;
        try {
            const session = await currentSession();
            token = session.getIdToken().getJwtToken();
        } catch {
            history.push(Path.Login);
            return;
        }

        const uploadedUrls: string[] = [];
        for (const imageFile of imageFiles) {
            try {
                const imageKey = `${shopID}/bookings/${uuid()}`;
                await imageStore.put(imageKey, imageFile, {
                    contentType: imageFile.type,
                });
                const imageUrl = `https://${process.env.REACT_APP_S3_IMAGE_UPLOAD_BUCKET_NAME}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/public/${imageKey}`;
                uploadedUrls.push(imageUrl);
            } catch {
                setState((prev) => ({
                    ...prev,
                    error: "Failed to upload image. Please try again.",
                }));
                return;
            }
        }

        const bookingWithImages: Booking = {
            ...booking,
            images: uploadedUrls.length > 0 ? uploadedUrls : undefined,
        };

        try {
            const response = await addBooking({
                identityToken: token,
                shopID: shopID,
                booking: bookingWithImages,
            });

            if (response.statusCode !== 201 || !response.booking) {
                setState((prev) => ({
                    ...prev,
                    error: "Failed to add booking. Please try again.",
                }));
                return;
            }

            const newBooking: Booking = {
                ...bookingWithImages,
                id: response.booking!.id,
            };
            setState((prev) => ({
                ...prev,
                bookings: [...prev.bookings, newBooking],
                filteredBookings: [...prev.filteredBookings, newBooking],
                activity: Activity.List,
            }));
        } catch (err) {
            setState((prev) => ({
                ...prev,
                error: "Failed to add booking. Please try again.",
            }));
        }
    };

    const handleUpdateBooking = async (booking: Booking, existingUrls: string[] = [], newImageFiles: File[] = []) => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) {
            setState((prev) => ({
                ...prev,
                error: "Shop could not be found.",
            }));
            return;
        }

        let token: string;
        try {
            const session = await currentSession();
            token = session.getIdToken().getJwtToken();
        } catch {
            history.push(Path.Login);
            return;
        }

        const uploadedUrls: string[] = [];
        for (const imageFile of newImageFiles) {
            try {
                const imageKey = `${shopID}/bookings/${uuid()}`;
                await imageStore.put(imageKey, imageFile, {
                    contentType: imageFile.type,
                });
                const imageUrl = `https://${process.env.REACT_APP_S3_IMAGE_UPLOAD_BUCKET_NAME}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/public/${imageKey}`;
                uploadedUrls.push(imageUrl);
            } catch {
                setState((prev) => ({
                    ...prev,
                    error: "Failed to upload image. Please try again.",
                }));
                return;
            }
        }

        const allImageUrls = [...existingUrls, ...uploadedUrls];
        const bookingWithImages: Booking = {
            ...booking,
            images: allImageUrls.length > 0 ? allImageUrls : undefined,
        };

        try {
            const response = await updateBooking({
                identityToken: token,
                shopID: shopID,
                booking: bookingWithImages,
            });

            if (response.statusCode !== 200) {
                setState((prev) => ({
                    ...prev,
                    error: "Failed to update booking. Please try again.",
                }));
                return;
            }

            setState((prev) => ({
                ...prev,
                bookings: prev.bookings.map((b) =>
                    b.id === bookingWithImages.id ? bookingWithImages : b
                ),
                filteredBookings: prev.filteredBookings.map((b) =>
                    b.id === bookingWithImages.id ? bookingWithImages : b
                ),
                activity: Activity.List,
                activeBookingId: null,
            }));
        } catch (err) {
            setState((prev) => ({
                ...prev,
                error: "Failed to update booking. Please try again.",
            }));
        }
    };

    const handleDeleteClick = (bookingId: string) => {
        setState((prev) => ({
            ...prev,
            deletingBookingId: bookingId,
        }));
    };

    const handleCancelDelete = () => {
        setState((prev) => ({
            ...prev,
            deletingBookingId: null,
        }));
    };

    const handleConfirmDelete = async () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        const bookingId = state.deletingBookingId;

        if (!shopID || !bookingId) {
            return;
        }

        let token: string;
        try {
            const session = await currentSession();
            token = session.getIdToken().getJwtToken();
        } catch {
            history.push(Path.Login);
            return;
        }

        try {
            const response = await deleteBooking({
                identityToken: token,
                shopID: shopID,
                bookingID: bookingId,
            });

            if (response.statusCode !== 200) {
                setState((prev) => ({
                    ...prev,
                    error: "Failed to delete booking. Please try again.",
                    deletingBookingId: null,
                }));
                return;
            }

            setState((prev) => ({
                ...prev,
                bookings: prev.bookings.filter((b) => b.id !== bookingId),
                filteredBookings: prev.filteredBookings.filter((b) => b.id !== bookingId),
                deletingBookingId: null,
            }));
        } catch (err) {
            setState((prev) => ({
                ...prev,
                error: "Failed to delete booking. Please try again.",
                deletingBookingId: null,
            }));
        }
    };

    const handleToggleBooking = async (bookingId: string, enable: boolean) => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) {
            setState((prev) => ({
                ...prev,
                error: "Shop could not be found.",
            }));
            return;
        }

        // Optimistically flip the toggle before the API call
        setState((prev) => ({
            ...prev,
            bookings: prev.bookings.map((b) =>
                b.id === bookingId ? { ...b, disabled: !enable } : b
            ),
            filteredBookings: prev.filteredBookings.map((b) =>
                b.id === bookingId ? { ...b, disabled: !enable } : b
            ),
        }));

        const revert = () => {
            setState((prev) => ({
                ...prev,
                bookings: prev.bookings.map((b) =>
                    b.id === bookingId ? { ...b, disabled: enable } : b
                ),
                filteredBookings: prev.filteredBookings.map((b) =>
                    b.id === bookingId ? { ...b, disabled: enable } : b
                ),
            }));
        };

        let token: string;
        try {
            const session = await currentSession();
            token = session.getIdToken().getJwtToken();
        } catch {
            revert();
            history.push(Path.Login);
            return;
        }

        try {
            const response = await toggleBooking({
                identityToken: token,
                shopID: shopID,
                bookingID: bookingId,
                enable: enable,
            });

            if (response.statusCode !== 200) {
                revert();
                setState((prev) => ({
                    ...prev,
                    error: "Failed to toggle booking. Please try again.",
                }));
            }
        } catch (err) {
            revert();
            setState((prev) => ({
                ...prev,
                error: "Failed to toggle booking. Please try again.",
            }));
        }
    };

    const activeBooking = state.activeBookingId
        ? state.bookings.find((b) => b.id === state.activeBookingId) || null
        : null;

    return (
        <div>
            <Modal
                show={state.activity === Activity.Add}
                onHide={() => changeActivity(Activity.List)}
                backdrop="static"
            >
                <Modal.Header>
                    <Modal.Title>Add Booking</Modal.Title>
                    <div style={{ flex: 1, textAlign: "right" }}>
                        <Button
                            icon={icon.Close}
                            buttonvariant={variant.Secondary}
                            onClick={() => changeActivity(Activity.List)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" show={state.error !== ""}>
                        {state.error}
                    </Alert>
                    <BookingForm
                        booking={null}
                        onSave={handleAddBooking}
                        onCancel={() => changeActivity(Activity.List)}
                    />
                </Modal.Body>
            </Modal>

            <Modal
                show={state.activity === Activity.View}
                onHide={() => changeActivity(Activity.List)}
                backdrop="static"
            >
                <Modal.Header>
                    <Modal.Title>Booking</Modal.Title>
                    <div style={{ flex: 1, textAlign: "right" }}>
                        <Button
                            icon={icon.Close}
                            buttonvariant={variant.Secondary}
                            onClick={() => changeActivity(Activity.List)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" show={state.error !== ""}>
                        {state.error}
                    </Alert>
                    <BookingViewEdit
                        booking={activeBooking}
                        onSave={handleUpdateBooking}
                        onCancel={() => changeActivity(Activity.List)}
                    />
                </Modal.Body>
            </Modal>

            <DeleteConfirmationModal
                show={state.deletingBookingId !== null}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />

            <Row>
                <h1 style={{ marginBottom: "2rem" }}>Bookings</h1>
            </Row>

            <Row>
                <Col xs={12} md={3} xxl={2} style={{ textAlign: "left", marginBottom: "1rem" }}>
                    <Button
                        style={{ width: "100%" }}
                        buttonvariant={variant.Primary}
                        name="Add Booking"
                        onClick={() => changeActivity(Activity.Add)}
                    />
                </Col>
                <Col xs={12} md={9} xxl={10} style={{ marginBottom: "1rem" }}>
                    <BookingSearch onChange={handleBookingSearch} />
                </Col>
            </Row>

            <p style={{ textAlign: "center", marginTop: "1rem" }}>Bookings</p>

            <Alert variant="danger" show={state.error !== "" && state.activity === Activity.List}>
                {state.error}
            </Alert>

            {state.isLoading && (
                <div
                    data-testid="bookings-loading"
                    style={{ textAlign: "center" }}
                >
                    <Spinner variant="success" animation="border" />
                </div>
            )}

            {!state.isLoading && (
                <>
                    {!state.bookings || state.bookings.length === 0 ? (
                        <EmptyBookings />
                    ) : (
                        <Row style={{ marginTop: "-1rem" }}>
                            {state.filteredBookings.map((booking) => (
                                <Col
                                    key={booking.id}
                                    xs={12}
                                    md={4}
                                    xl={3}
                                    style={{ marginTop: "1rem" }}
                                >
                                    <BookingCard
                                        booking={booking}
                                        onSelect={handleSelectBooking}
                                        onDelete={handleDeleteClick}
                                        onToggle={handleToggleBooking}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

interface BookingCardProps {
    booking: Booking;
    onSelect: (bookingId: string) => void;
    onDelete: (bookingId: string) => void;
    onToggle: (bookingId: string, enable: boolean) => void;
}

const BookingCard: React.FC<BookingCardProps> = ({
    booking,
    onSelect,
    onDelete,
    onToggle,
}) => {
    return (
        <Card style={{ border: 0, height: "100%" }}>
            <a
                key={booking.id!}
                id={`booking-${booking.id!}`}
                onClick={() => onSelect(booking.id!)}
                style={{ cursor: "pointer" }}
                data-testid={`view-booking-${booking.id}`}
            >
                {booking.images && booking.images.length > 0 ? (
                    <img
                        src={booking.images[0]}
                        alt={booking.name}
                        style={{
                            width: "100%",
                            height: "200px",
                            objectFit: "cover",
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: "100%",
                            height: "200px",
                            backgroundColor: Color.LightGrey,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.2rem",
                            color: Color.Grey,
                            fontWeight: "bold",
                        }}
                    >
                        {booking.name.charAt(0).toUpperCase()}
                    </div>
                )}
            </a>
            <Card.Body style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                <Card.Title>{booking.name}</Card.Title>
                <Card.Text style={{ marginTop: ".8rem" }}>
                    £{Number(booking.price).toFixed(2)}
                </Card.Text>
                <div>
                    <Form.Check
                        type="switch"
                        id={`toggle-booking-${booking.id}`}
                        data-testid={`toggle-booking-${booking.id}`}
                        checked={!booking.disabled}
                        onChange={(e) => onToggle(booking.id!, e.target.checked)}
                        style={{ display: "inline-block", marginRight: "0.5rem" }}
                    />
                    <BButton
                        data-testid={`delete-booking-${booking.id}`}
                        style={{
                            backgroundColor: Color.Red,
                            border: 0,
                        }}
                        onClick={() => onDelete(booking.id!)}
                    >
                        <FaTrash />
                    </BButton>
                </div>
            </Card.Body>
        </Card>
    );
};

const EmptyBookings: React.FC = () => {
    return (
        <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>No bookings yet. Create your first booking service above.</p>
        </div>
    );
};

interface BookingSearchProps {
    onChange: (input: string) => void;
}

const BookingSearch: React.FC<BookingSearchProps> = ({ onChange }) => {
    return (
        <div className="input-group" style={{ width: "100%", margin: "auto" }}>
            <div className="form-outline" style={{ width: "100%", display: "flex" }}>
                <div className="form-outline flex-grow-1">
                    <input
                        style={{ borderRadius: 0 }}
                        onChange={(e) => onChange(e.target.value)}
                        type="search"
                        id="booking-search-bar"
                        className="form-control"
                        placeholder="Search for a booking"
                    />
                </div>
                <Button
                    onClick={() => { }}
                    buttonvariant={variant.Primary}
                    icon={icon.Search}
                />
            </div>
        </div>
    );
};

const DAYS_OF_WEEK: Array<{ key: WeeklySchedule["day"]; label: string }> = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
];

interface AvailabilityEditorProps {
    availability: Availability;
    onChange: (availability: Availability) => void;
}

const AvailabilityEditor: React.FC<AvailabilityEditorProps> = ({
    availability,
    onChange,
}) => {
    const weekly = availability?.weekly || [];

    const getScheduleForDay = (day: WeeklySchedule["day"]): WeeklySchedule | undefined => {
        return weekly.find((s) => s.day === day);
    };

    const handleDayToggle = (day: WeeklySchedule["day"]) => {
        const existing = getScheduleForDay(day);
        if (existing) {
            onChange({
                ...availability,
                weekly: weekly.filter((s) => s.day !== day),
            });
        } else {
            onChange({
                ...availability,
                weekly: [
                    ...weekly,
                    { day, times: [{ start: "09:00", end: "17:00" }] },
                ],
            });
        }
    };

    const handleTimeSlotChange = (
        day: WeeklySchedule["day"],
        slotIndex: number,
        field: "start" | "end",
        value: string
    ) => {
        onChange({
            ...availability,
            weekly: weekly.map((schedule) => {
                if (schedule.day !== day) return schedule;
                return {
                    ...schedule,
                    times: (schedule.times || []).map((slot, idx) =>
                        idx === slotIndex ? { ...slot, [field]: value } : slot
                    ),
                };
            }),
        });
    };

    const handleAddTimeSlot = (day: WeeklySchedule["day"]) => {
        onChange({
            ...availability,
            weekly: weekly.map((schedule) => {
                if (schedule.day !== day) return schedule;
                return {
                    ...schedule,
                    times: [...(schedule.times || []), { start: "09:00", end: "17:00" }],
                };
            }),
        });
    };

    const handleRemoveTimeSlot = (day: WeeklySchedule["day"], slotIndex: number) => {
        onChange({
            ...availability,
            weekly: weekly.map((schedule) => {
                if (schedule.day !== day) return schedule;
                return {
                    ...schedule,
                    times: (schedule.times || []).filter((_, idx) => idx !== slotIndex),
                };
            }),
        });
    };

    const handleLeadTimeChange = (hours: number) => {
        onChange({
            ...availability,
            leadTimeHours: hours,
        });
    };

    return (
        <div style={{ marginBottom: "1rem" }}>
            <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600, fontSize: "1rem" }}>
                    Lead Time (hours)
                </Form.Label>
                <Form.Control
                    type="number"
                    min="0"
                    value={availability?.leadTimeHours || 0}
                    onChange={(e) => handleLeadTimeChange(parseInt(e.target.value) || 0)}
                    data-testid="availability-lead-time"
                    style={{ fontSize: "1rem", padding: "0.75rem" }}
                />
                <Form.Text className="text-muted">
                    Minimum hours notice required for a booking
                </Form.Text>
            </Form.Group>

            <Form.Label style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "0.75rem", display: "block" }}>
                Available Days & Time Slots
            </Form.Label>
            {DAYS_OF_WEEK.map(({ key, label }) => {
                const schedule = getScheduleForDay(key);
                const isEnabled = !!schedule;

                return (
                    <div
                        key={key}
                        style={{
                            border: isEnabled ? `2px solid ${Color.Primary}` : "1px solid #dee2e6",
                            borderRadius: "0.5rem",
                            marginBottom: "0.75rem",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            onClick={() => handleDayToggle(key)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "1rem",
                                backgroundColor: isEnabled ? `${Color.Primary}1A` : "#f8f9fa",
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                            data-testid={`availability-day-${key}`}
                        >
                            <Form.Check
                                type="checkbox"
                                id={`day-${key}`}
                                checked={isEnabled}
                                onChange={() => {}}
                                style={{ marginRight: "0.75rem", transform: "scale(1.3)" }}
                            />
                            <span style={{ fontWeight: 500, fontSize: "1.1rem" }}>{label}</span>
                        </div>
                        {isEnabled && schedule && (
                            <div style={{ padding: "1rem", backgroundColor: "#fff" }}>
                                {(schedule.times || []).map((slot, slotIndex) => (
                                    <div
                                        key={slotIndex}
                                        style={{
                                            backgroundColor: "#f8f9fa",
                                            borderRadius: "0.5rem",
                                            padding: "1rem",
                                            marginBottom: "0.75rem",
                                        }}
                                    >
                                        <Row className="g-2 align-items-center">
                                            <Col xs={12} sm={5}>
                                                <Form.Label
                                                    style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
                                                >
                                                    Start
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={slot.start || ""}
                                                    onChange={(e) =>
                                                        handleTimeSlotChange(key, slotIndex, "start", e.target.value)
                                                    }
                                                    style={{ fontSize: "1rem", padding: "0.75rem" }}
                                                    data-testid={`availability-${key}-slot-${slotIndex}-start`}
                                                />
                                            </Col>
                                            <Col xs={12} sm={5}>
                                                <Form.Label
                                                    style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
                                                >
                                                    End
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={slot.end || ""}
                                                    onChange={(e) =>
                                                        handleTimeSlotChange(key, slotIndex, "end", e.target.value)
                                                    }
                                                    style={{ fontSize: "1rem", padding: "0.75rem" }}
                                                    data-testid={`availability-${key}-slot-${slotIndex}-end`}
                                                />
                                            </Col>
                                            <Col xs={12} sm={2} className="d-flex align-items-end">
                                                {(schedule.times || []).length > 1 && (
                                                    <BButton
                                                        variant="outline-danger"
                                                        onClick={() => handleRemoveTimeSlot(key, slotIndex)}
                                                        data-testid={`availability-${key}-slot-${slotIndex}-remove`}
                                                        style={{
                                                            width: "100%",
                                                            padding: "0.75rem",
                                                            marginTop: "0.25rem",
                                                        }}
                                                    >
                                                        <FaTrash />
                                                    </BButton>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <BButton
                                    variant="outline-primary"
                                    onClick={() => handleAddTimeSlot(key)}
                                    data-testid={`availability-${key}-add-slot`}
                                    style={{
                                        width: "100%",
                                        padding: "0.75rem",
                                        fontSize: "1rem",
                                    }}
                                >
                                    + Add Time Slot
                                </BButton>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

interface BookingFormProps {
    booking: Booking | null;
    onSave: (booking: Booking, imageFiles: File[]) => void;
    onCancel: () => void;
}

const defaultAvailability: Availability = {
    weekly: [],
    overrides: [],
    leadTimeHours: 24,
};

const BookingForm: React.FC<BookingFormProps> = ({
    booking,
    onSave,
    onCancel,
}) => {
    const [name, setName] = useState(booking?.name || "");
    const [price, setPrice] = useState(booking?.price?.toString() || "");
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [availability, setAvailability] = useState<Availability>(
        booking?.availability || defaultAvailability
    );

    useEffect(() => {
        if (booking) {
            setName(booking.name);
            setPrice(booking.price.toString());
            setAvailability(booking.availability || defaultAvailability);
        } else {
            setName("");
            setPrice("");
            setAvailability(defaultAvailability);
        }
        setImageFiles([]);
    }, [booking]);

    const handleImagesChange = (_urls: string[], files: File[]) => {
        setImageFiles(files);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newBooking: Booking = {
            id: booking?.id,
            name: name,
            price: parseFloat(price) || 0,
            categories: booking?.categories || [],
            availability: availability,
        };
        onSave(newBooking, imageFiles);
    };

    return (
        <Form onSubmit={handleSubmit} data-testid="booking-form">
            <MultiImageUploader
                label="Upload booking images"
                onImagesChange={handleImagesChange}
            />
            <Form.Group className="mb-3">
                <Form.Label htmlFor="booking-name">Name</Form.Label>
                <Form.Control
                    id="booking-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label htmlFor="booking-price">Price</Form.Label>
                <Form.Control
                    id="booking-price"
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                />
            </Form.Group>
            <AvailabilityEditor
                availability={availability}
                onChange={setAvailability}
            />
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.5rem",
                }}
            >
                <BButton
                    data-testid="booking-form-cancel"
                    type="button"
                    style={{
                        backgroundColor: Color.Grey,
                        border: 0,
                        borderRadius: 0,
                    }}
                    onClick={onCancel}
                >
                    Cancel
                </BButton>
                <BButton
                    data-testid="booking-form-submit"
                    type="submit"
                    style={{
                        backgroundColor: Color.Primary,
                        border: 0,
                        borderRadius: 0,
                    }}
                >
                    Save
                </BButton>
            </div>
        </Form>
    );
};

interface BookingViewEditProps {
    booking: Booking | null;
    onSave: (booking: Booking, existingUrls: string[], newImageFiles: File[]) => void;
    onCancel: () => void;
}

const BookingViewEdit: React.FC<BookingViewEditProps> = ({
    booking,
    onSave,
    onCancel,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(booking?.name || "");
    const [price, setPrice] = useState(booking?.price?.toString() || "");
    const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
    const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
    const [availability, setAvailability] = useState<Availability>(
        booking?.availability || defaultAvailability
    );

    useEffect(() => {
        if (booking) {
            setName(booking.name);
            setPrice(booking.price.toString());
            setExistingImageUrls(booking.images || []);
            setAvailability(booking.availability || defaultAvailability);
        }
        setIsEditing(false);
        setNewImageFiles([]);
    }, [booking]);

    if (!booking) {
        return null;
    }

    const handleImagesChange = (urls: string[], files: File[]) => {
        setExistingImageUrls(urls);
        setNewImageFiles(files);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const updatedBooking: Booking = {
            ...booking,
            name: name,
            price: parseFloat(price) || 0,
            availability: availability,
        };
        onSave(updatedBooking, existingImageUrls, newImageFiles);
    };

    const getAllImages = (): string[] => {
        return booking.images || [];
    };

    if (isEditing) {
        return (
            <Form onSubmit={handleSubmit} data-testid="booking-edit-form">
                <MultiImageUploader
                    label="Upload booking images"
                    initialImages={getAllImages()}
                    onImagesChange={handleImagesChange}
                />
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="booking-name">Name</Form.Label>
                    <Form.Control
                        id="booking-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="booking-price">Price</Form.Label>
                    <Form.Control
                        id="booking-price"
                        type="number"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        required
                    />
                </Form.Group>
                <AvailabilityEditor
                    availability={availability}
                    onChange={setAvailability}
                />
                <div style={{ marginBottom: "1rem" }}>
                    <strong>Categories:</strong>{" "}
                    {booking.categories && booking.categories.length > 0
                        ? booking.categories.map((cat, idx) => (
                            <Badge
                                key={idx}
                                bg="secondary"
                                style={{ marginRight: "0.25rem" }}
                            >
                                {cat}
                            </Badge>
                        ))
                        : "None"}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "0.5rem",
                    }}
                >
                    <BButton
                        data-testid="booking-edit-cancel"
                        type="button"
                        style={{
                            backgroundColor: Color.Grey,
                            border: 0,
                            borderRadius: 0,
                        }}
                        onClick={() => {
                            setName(booking.name);
                            setPrice(booking.price.toString());
                            setNewImageFiles([]);
                            setExistingImageUrls(getAllImages());
                            setAvailability(booking.availability || defaultAvailability);
                            setIsEditing(false);
                        }}
                    >
                        Cancel
                    </BButton>
                    <BButton
                        data-testid="booking-edit-save"
                        type="submit"
                        style={{
                            backgroundColor: Color.Primary,
                            border: 0,
                            borderRadius: 0,
                        }}
                    >
                        Save
                    </BButton>
                </div>
            </Form>
        );
    }

    const allImages = getAllImages();

    return (
        <div data-testid="booking-view">
            {allImages.length > 0 && (
                <Row className="mb-3">
                    {allImages.map((imgUrl, idx) => (
                        <Col key={idx} xs={6} md={4} className="mb-2">
                            <img
                                src={imgUrl}
                                alt={`Image ${idx + 1}`}
                                style={{
                                    width: "100%",
                                    aspectRatio: "1/1",
                                    objectFit: "cover",
                                    borderRadius: "0.25rem",
                                }}
                            />
                        </Col>
                    ))}
                </Row>
            )}
            <div style={{ marginBottom: "1rem" }}>
                <strong>Name:</strong> {booking.name}
            </div>
            <div style={{ marginBottom: "1rem" }}>
                <strong>Price:</strong> £{Number(booking.price).toFixed(2)}
            </div>
            <div style={{ marginBottom: "1rem" }}>
                <strong>Categories:</strong>{" "}
                {booking.categories && booking.categories.length > 0
                    ? booking.categories.map((cat, idx) => (
                        <Badge
                            key={idx}
                            bg="secondary"
                            style={{ marginRight: "0.25rem" }}
                        >
                            {cat}
                        </Badge>
                    ))
                    : "None"}
            </div>
            <div style={{ marginBottom: "1rem" }}>
                <strong>Lead Time:</strong> {booking.availability?.leadTimeHours || 0} hours
            </div>
            <div style={{ marginBottom: "1rem" }}>
                <strong>Availability:</strong>
                {booking.availability?.weekly && booking.availability.weekly.length > 0 ? (
                    <ul style={{ marginTop: "0.5rem", paddingLeft: "1.5rem" }}>
                        {booking.availability.weekly.map((schedule, idx) => (
                            <li key={idx}>
                                <span style={{ textTransform: "capitalize" }}>{schedule.day}</span>:{" "}
                                {(schedule.times || []).map((slot, slotIdx) => (
                                    <span key={slotIdx}>
                                        {slot.start || ""} - {slot.end || ""}
                                        {slotIdx < (schedule.times || []).length - 1 ? ", " : ""}
                                    </span>
                                ))}
                            </li>
                        ))}
                    </ul>
                ) : (
                    " Not set"
                )}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.5rem",
                }}
            >
                <BButton
                    data-testid="booking-view-close"
                    type="button"
                    style={{
                        backgroundColor: Color.Grey,
                        border: 0,
                        borderRadius: 0,
                    }}
                    onClick={onCancel}
                >
                    Close
                </BButton>
                <BButton
                    data-testid="booking-view-edit"
                    type="button"
                    style={{
                        backgroundColor: Color.Primary,
                        border: 0,
                        borderRadius: 0,
                    }}
                    onClick={() => setIsEditing(true)}
                >
                    <FaEdit style={{ marginRight: "0.5rem" }} />
                    Edit
                </BButton>
            </div>
        </div>
    );
};

interface DeleteConfirmationModalProps {
    show: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    show,
    onConfirm,
    onCancel,
}) => {
    return (
        <Modal show={show} onHide={onCancel}>
            <Modal.Header>
                <Modal.Title>Delete Booking</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Are you sure you want to delete this booking? This action cannot
                be undone.
            </Modal.Body>
            <Modal.Footer>
                <Button
                    buttonvariant={variant.Secondary}
                    name="Cancel"
                    onClick={onCancel}
                />
                <Button
                    buttonvariant={variant.Danger}
                    name="Confirm"
                    onClick={onConfirm}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default Bookings;
